import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useMutation, type QueryClient } from '@tanstack/react-query';
import type {
  CsvFileUploadState,
  ParsedCsvRow,
  TabViewMode,
} from '~/app/components/knowledgeBase/componentSchemaManager/ComponentSchemaManager.types';
import { CSV_FILE_CONFIGS } from '~/app/constants/csvAllowedFields';
import { ComponentSchemaService } from '~/app/services/knowledgeBase';
import {
  parseCsvText,
  validateCsv,
  MAX_FILE_SIZE,
  createInitialUploads,
  createInitialViewModes,
} from './csvUtils';
import { createCsvFileFromRows } from '~/app/utils/convertToCsv';

// ── Hook ────────────────────────────────────────────────────────────

export const useCsvFileUpload = (
  queryClient: QueryClient,
  paginationState: Record<
    string,
    { current: number; pageSize: number; total?: number }
  >,
  setPagination: React.Dispatch<
    React.SetStateAction<
      Record<string, { current: number; pageSize: number; total?: number }>
    >
  >,
) => {
  const { t } = useTranslation('componentSchema');

  const uploadMutation = useMutation({
    mutationFn: (payload: {
      configName: string;
      data?: ParsedCsvRow[];
      file?: File;
    }) =>
      ComponentSchemaService.uploadCsv(
        payload.configName,
        payload.data,
        payload.file,
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['lookup', variables.configName],
      });
    },
  });

  const [uploads, setUploads] = useState<Record<string, CsvFileUploadState>>(
    () => createInitialUploads(CSV_FILE_CONFIGS),
  );
  const [viewModes, setViewModes] = useState<Record<string, TabViewMode>>(() =>
    createInitialViewModes(CSV_FILE_CONFIGS),
  );
  const [submitting, setSubmitting] = useState<Record<string, boolean>>({});

  // ── View mode ───────────────────────────────────────────────────

  const handleToggleView = useCallback(
    (configName: string, viewMode: TabViewMode) => {
      setViewModes((prev) => ({
        ...prev,
        [configName]: viewMode,
      }));
    },
    [],
  );

  // ── File parsing ────────────────────────────────────────────────

  const handleFileUpload = useCallback(
    (configName: string, file: File) => {
      const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
      if (!config) return;

      if (file.size > MAX_FILE_SIZE) {
        setUploads((prev) => ({
          ...prev,
          [configName]: {
            ...prev[configName],
            file,
            status: 'error',
            errors: [t('messages.fileSizeExceeded')],
            data: [],
            fileName: file.name,
          },
        }));
        return;
      }

      if (!file.name.toLowerCase().endsWith('.csv')) {
        setUploads((prev) => ({
          ...prev,
          [configName]: {
            ...prev[configName],
            file,
            status: 'error',
            errors: [t('messages.onlyCsvAccepted')],
            data: [],
            fileName: file.name,
          },
        }));
        return;
      }

      setUploads((prev) => ({
        ...prev,
        [configName]: {
          ...prev[configName],
          file,
          status: 'parsing',
          errors: [],
          data: [],
          fileName: file.name,
        },
      }));

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const { headers, rows } = parseCsvText(text);
        const errors = validateCsv(config, headers, rows);

        if (errors.length > 0) {
          setUploads((prev) => ({
            ...prev,
            [configName]: {
              ...prev[configName],
              status: 'error',
              errors,
              data: [],
            },
          }));
          return;
        }

        const data: ParsedCsvRow[] = rows.map((row) => {
          const obj: ParsedCsvRow = {};
          headers.forEach((header, i) => {
            obj[header] = row[i] || '';
          });
          return obj;
        });

        setUploads((prev) => ({
          ...prev,
          [configName]: {
            ...prev[configName],
            status: 'success',
            errors: [],
            data,
            fileName: file.name,
          },
        }));

        message.success(
          t('messages.parsedSuccess', {
            fileName: file.name,
            count: data.length,
          }),
        );
      };

      reader.onerror = () => {
        setUploads((prev) => ({
          ...prev,
          [configName]: {
            ...prev[configName],
            status: 'error',
            errors: [t('messages.failedToRead')],
            data: [],
          },
        }));
      };

      reader.readAsText(file);
    },
    [t],
  );

  const handleFileRemove = useCallback((configName: string) => {
    const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
    setUploads((prev) => ({
      ...prev,
      [configName]: {
        file: null,
        status: 'idle',
        data: [],
        errors: [],
        fileName:
          config?.fileName ||
          `${configName.toLowerCase().replace(/ /g, '_')}.csv`,
      },
    }));
  }, []);

  const handleDownloadTemplate = useCallback(
    (configName: string) => {
      const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
      if (!config) return;

      const csvColumns = config.columns.filter((c) => !c.excludeFromCsv);
      const header = csvColumns.map((c) => c.key).join(',');
      const sampleRow = csvColumns
        .map((c) => {
          if (c.key === 'code') return '0';
          if (c.key === 'name') return 'EXAMPLE_NAME';
          if (c.key === 'description') return 'Example description';
          if (c.key === 'unit') return 'UNIT_UNKNOWN';
          return '';
        })
        .join(',');

      const csv = `${header}\n${sampleRow}\n`;
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName =
        config.fileName ||
        `${config.name.toLowerCase().replace(/ /g, '_')}.csv`;
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      message.info(t('messages.templateDownloaded', { fileName }));
    },
    [t],
  );

  // ── Submit CSV to DB ────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (configName: string) => {
      const upload = uploads[configName];

      if (!upload || upload.status !== 'success') {
        message.warning(t('messages.uploadValidFirst'));
        return;
      }

      setSubmitting((prev) => ({ ...prev, [configName]: true }));

      try {
        const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
        if (!config) return;

        if (configName === 'Event State') {
          const correctedFile =
            upload.data.length > 0
              ? createCsvFileFromRows(upload.data, 'event_state_corrected.csv')
              : upload.file;

          if (!correctedFile) {
            message.warning(t('messages.uploadValidFirst'));
            return;
          }

          await uploadMutation.mutateAsync({ configName, file: correctedFile });
          queryClient.invalidateQueries({
            queryKey: ['event-state-import-history'],
          });
        } else {
          await uploadMutation.mutateAsync({ configName, data: upload.data });
        }

        const pg = paginationState[configName] || { current: 1, pageSize: 10 };
        setPagination((prev) => ({
          ...prev,
          [configName]: { ...pg },
        }));

        setUploads((prev) => ({
          ...prev,
          [configName]: {
            file: null,
            status: 'idle',
            data: [],
            errors: [],
            fileName: prev[configName]?.fileName || '',
          },
        }));

        setViewModes((prev) => ({ ...prev, [configName]: 'crud' }));

        message.success(
          t('messages.submitSuccess', { fileName: upload.fileName }),
        );
      } catch {
        message.error(t('messages.submitFailed'));
      } finally {
        setSubmitting((prev) => ({ ...prev, [configName]: false }));
      }
    },
    [uploads, uploadMutation, paginationState, queryClient, setPagination, t],
  );

  // ── Matchy submit ───────────────────────────────────────────────

  const handleMatchySubmit = useCallback(
    async (configName: string, data: ParsedCsvRow[]) => {
      if (!data || data.length === 0) return;

      setSubmitting((prev) => ({ ...prev, [configName]: true }));

      try {
        const config = CSV_FILE_CONFIGS.find((c) => c.name === configName);
        if (!config) return;

        if (configName === 'Event State') {
          const correctedFile = createCsvFileFromRows(
            data,
            'event_state_corrected.csv',
          );
          await uploadMutation.mutateAsync({
            configName,
            file: correctedFile,
          });
          queryClient.invalidateQueries({
            queryKey: ['event-state-import-history'],
          });
        } else {
          await uploadMutation.mutateAsync({ configName, data });
        }

        setViewModes((prev) => ({ ...prev, [configName]: 'crud' }));

        message.success(
          t('messages.submitSuccess', { fileName: config.fileName }),
        );
      } catch {
        message.error(t('messages.submitFailed'));
      } finally {
        setSubmitting((prev) => ({ ...prev, [configName]: false }));
      }
    },
    [queryClient, uploadMutation, t],
  );

  return {
    uploads,
    viewModes,
    submitting,
    onToggleView: handleToggleView,
    onFileUpload: handleFileUpload,
    onFileRemove: handleFileRemove,
    onDownloadTemplate: handleDownloadTemplate,
    onSubmit: handleSubmit,
    onMatchySubmit: handleMatchySubmit,
  };
};
